import React, { useMemo } from "react";
import { Form } from "antd";
import Button from "components/BaseWidgets/button";
import Divider from "components/BaseWidgets/divider";
import Select from "components/BaseWidgets/select";
import PremiumForm from "../premiumForm";
import "./style.scss";
import { useSaveSearch } from "hooks/api/useSearch";
import { useNavigate } from "react-router-dom";
import { usePremium } from "context/PremiumContext";
import AlertNotification from "components/CustomWidgets/alertNotification";
import { useCourses } from "hooks/api/useListing";
import { mapToSelectOptions } from "utils/formatHelper";

function FormSearches({
  setOpen,
  selectOptions: initialSelectOptions = [],
  searchData,
  setSearchData,
}) {
  const { isPremium } = usePremium();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const qualificationValues = Form.useWatch("qualification", form);

  const { data: dynamicCourseData } = useCourses({
    parent_id: qualificationValues,
  });

  const selectOptions = useMemo(() => {
    return {
      ...initialSelectOptions,
      education: mapToSelectOptions(dynamicCourseData),
    };
  }, [initialSelectOptions, dynamicCourseData]);

  // search api
  const { mutate: saveMutation } = useSaveSearch({
    onSuccess: (data) => {
      const savedSearchId = data?.data?.saved_search_id;
      if (savedSearchId) {
        const params = new URLSearchParams();

        Object.entries(searchData).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, v));
          } else if (value !== undefined && value !== null) {
            params.set(key, value);
          }
        });

        params.set("st", 2);

        navigate(`/s/${savedSearchId}?${params.toString()}`);
        setOpen(false);
      }
    },
  });

  const handleSubmit = (values) => {
    const formattedValues = {
      ...values,
      age: values.age ? `${values.age.from}-${values.age.to}` : null,
      height: values.height
        ? `${values.height.from}cm-${values.height.to}cm`
        : null,
      weight: values.weight
        ? `${values.weight.from}Kg-${values.weight.to}Kg`
        : null,
    };

    // Remove the individual from/to properties
    delete formattedValues.age?.from;
    delete formattedValues.age?.to;
    delete formattedValues.height?.from;
    delete formattedValues.height?.to;
    delete formattedValues.weight?.from;
    delete formattedValues.weight?.to;

    // Recursive function to clean object
    const cleanObject = (obj, isPremium) => {
      const cleaned = {};

      for (const [key, value] of Object.entries(obj)) {
        // Skip default values for non-premium users
        if (
          !isPremium &&
          ((key === "disability_filter" && value === "No") ||
            (key === "drink_filter" && value === 2) ||
            (key === "smoke_filter" && value === 2))
        ) {
          continue;
        }

        if (
          value === null ||
          value === undefined ||
          value === "" ||
          (typeof value === "string" && value.includes("undefined"))
        ) {
          continue;
        }

        if (Array.isArray(value) && value.length === 0) {
          continue;
        }

        if (typeof value === "object" && !Array.isArray(value)) {
          const nestedCleaned = cleanObject(value, isPremium);
          if (Object.keys(nestedCleaned).length > 0) {
            cleaned[key] = nestedCleaned;
          }
        } else {
          cleaned[key] = value;
        }
      }

      return cleaned;
    };

    const availableData = cleanObject(formattedValues, isPremium);

    if (Object.keys(availableData).length === 0) {
      AlertNotification(
        "warning",
        "Please select at least one filter to search.",
      );
      return;
    }

    setSearchData(availableData);
    saveMutation({
      search_type: 2, // keyword - 1, Advanced - 2
      search_mode: 1, // 1 = auto, 2 = manual
      filter_data: availableData,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="form-searches"
      onFinish={handleSubmit}
    >
      {/* Age , Height & Weight Ranges */}
      {["Age", "Height"].map((label, idx) => (
        <Form.Item
          key={idx}
          label={<span className="font-title-xs">{label}</span>}
        >
          <div className="flex items-center gap-4 w-full">
            {/* From Select */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev[label.toLowerCase()]?.to !== curr[label.toLowerCase()]?.to
              }
            >
              {() => {
                const toValue = form.getFieldValue([label.toLowerCase(), "to"]);
                return (
                  <Form.Item name={[label.toLowerCase(), "from"]} noStyle>
                    <Select
                      widthVariant="wFull"
                      className="w-full"
                      placeholder="From"
                      options={(selectOptions[label.toLowerCase()] || []).map(
                        (opt) => ({
                          ...opt,
                          // disable values greater than current To
                          disabled: toValue
                            ? Number(opt.value) > Number(toValue)
                            : false,
                        }),
                      )}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <span className="font-body-lg fw-500">to</span>

            {/* To Select */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev[label.toLowerCase()]?.from !==
                curr[label.toLowerCase()]?.from
              }
            >
              {() => {
                const fromValue = form.getFieldValue([
                  label.toLowerCase(),
                  "from",
                ]);
                return (
                  <Form.Item
                    name={[label.toLowerCase(), "to"]}
                    noStyle
                    rules={[
                      {
                        validator: (_, toValue) => {
                          const fromValue = form.getFieldValue([
                            label.toLowerCase(),
                            "from",
                          ]);

                          if (
                            (fromValue && !toValue) ||
                            (!fromValue && toValue)
                          ) {
                            return Promise.reject(
                              new Error("Please select both From and To"),
                            );
                          }

                          if (
                            fromValue &&
                            toValue &&
                            Number(fromValue) > Number(toValue)
                          ) {
                            return Promise.reject(
                              new Error(
                                "Range is invalid: From cannot be greater than To",
                              ),
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      widthVariant="wFull"
                      className="w-full"
                      placeholder="To"
                      options={(selectOptions[label.toLowerCase()] || []).map(
                        (opt) => ({
                          ...opt,
                          // disable values smaller than current From
                          disabled: fromValue
                            ? Number(opt.value) < Number(fromValue)
                            : false,
                        }),
                      )}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </Form.Item>
      ))}

      <Divider />

      {/* Premium Form */}
      <PremiumForm selectOptions={selectOptions} form={form} />

      <Divider />

      {/* Submit Button */}
      <div className="btn-container">
        <Button className="search-btn" wFull htmlType="submit">
          Search
        </Button>
      </div>
    </Form>
  );
}

export default FormSearches;
